import { t } from "@lingui/macro";
import type { ErrorMessage } from "@reactive-resume/utils";
import { deepSearchAndParseDates } from "@reactive-resume/utils";
import _axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import { USAGE_ERROR, AUTH_ERROR } from "@reactive-resume/dto";

import { refreshToken } from "@/client/services/auth";
import { useAuthStore } from "@/client/stores/auth";

import { toast } from "../hooks/use-toast";
import { translateError } from "../services/errors/translate-error";

// Global state for usage limit errors
type UsageLimitError = {
  error: string;
  feature: string;
  resetIn: string;
};

// Global state for auth errors
type AuthError = {
  error: string;
  message: string;
  provider?: string;
  redirectUrl?: string;
};

// Global store for the latest usage limit error
// This is used by the dialog to display the error
let currentUsageLimitError: UsageLimitError | null = null;
let onUsageLimitErrorListeners: Array<(error: UsageLimitError) => void> = [];

// Global store for the latest auth error
let currentAuthError: AuthError | null = null;
let onAuthErrorListeners: Array<(error: AuthError) => void> = [];

// Global API for handling usage limit errors
export const usageLimitErrorAPI = {
  // Get the current error
  getCurrentError: () => currentUsageLimitError,

  // Set the current error
  setCurrentError: (error: UsageLimitError | null) => {
    currentUsageLimitError = error;
    if (error) {
      // Notify all listeners
      onUsageLimitErrorListeners.forEach((listener) => listener(error));
    }
  },

  // Add a listener for usage limit errors
  addListener: (listener: (error: UsageLimitError) => void) => {
    onUsageLimitErrorListeners.push(listener);
    return () => {
      onUsageLimitErrorListeners = onUsageLimitErrorListeners.filter((l) => l !== listener);
    };
  },

  // Clear all listeners
  clearListeners: () => {
    onUsageLimitErrorListeners = [];
  },
};

// Global API for handling auth errors
export const authErrorAPI = {
  // Get the current error
  getCurrentError: () => currentAuthError,

  // Set the current error
  setCurrentError: (error: AuthError | null) => {
    currentAuthError = error;
    if (error) {
      // Notify all listeners
      onAuthErrorListeners.forEach((listener) => listener(error));
    }
  },

  // Add a listener for auth errors
  addListener: (listener: (error: AuthError) => void) => {
    onAuthErrorListeners.push(listener);
    return () => {
      onAuthErrorListeners = onAuthErrorListeners.filter((l) => l !== listener);
    };
  },

  // Clear all listeners
  clearListeners: () => {
    onAuthErrorListeners = [];
  },
};

export const axios = _axios.create({ baseURL: "/api", withCredentials: true });

// Intercept responses to transform ISO dates to JS date objects
axios.interceptors.response.use(
  (response) => {
    const transformedResponse = deepSearchAndParseDates(response.data, ["createdAt", "updatedAt"]);
    return { ...response, data: transformedResponse };
  },
  (error) => {
    // Check if it's a usage limit error
    const errorData = error.response?.data;
    if (errorData?.error === USAGE_ERROR.LIMIT_REACHED) {
      // Set the current error
      usageLimitErrorAPI.setCurrentError({
        error: errorData.error,
        feature: errorData.feature,
        resetIn: errorData.resetIn,
      });
      return Promise.reject(errorData);
    }

    // Check if it's an anonymous access denied error
    if (errorData?.error === AUTH_ERROR.ANONYMOUS_ACCESS_DENIED) {
      // Set the current auth error with redirect URL
      authErrorAPI.setCurrentError({
        error: errorData.error,
        message: errorData.message,
        redirectUrl: window.location.pathname + window.location.search,
      });
      return Promise.reject(errorData);
    }

    // Check if it's an anonymous user no longer anonymous error
    if (errorData?.error === AUTH_ERROR.ANONYMOUS_USER_NO_LONGER_ANONYMOUS) {
      return Promise.reject(errorData);
    }

    // Don't show toasts for 404 errors
    if (error.response?.status === 404) {
      return Promise.reject(error);
    }

    // Don't show toasts for auth errors (401, 403) - these are handled by the refresh interceptor
    if (error.response?.status === 401 || error.response?.status === 403) {
      console.log("Auth error detected, will be handled by refresh interceptor");
      return Promise.reject(error);
    }

    // Don't show toasts when on the home page
    if (window.location.pathname === "/") {
      return Promise.reject(error);
    }

    const message = error.response?.data.message as ErrorMessage;
    const description = translateError(message);

    if (description) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description,
      });
    }

    return Promise.reject(new Error(message));
  },
);

// Create another instance to handle failed refresh tokens
// Reference: https://github.com/Flyrell/axios-auth-refresh/issues/191
const axiosForRefresh = _axios.create({ baseURL: "/api", withCredentials: true });

// Interceptor to handle expired access token errors
const handleAuthError = async () => {
  try {
    console.log("Attempting to refresh access token...");
    await refreshToken(axiosForRefresh);
    console.log("Access token refresh successful");
  } catch (error) {
    console.log("Access token refresh failed:", error);
    throw error;
  }
};

// Interceptor to handle expired refresh token errors
const handleRefreshError = async () => {
  console.log("Refresh token failed, clearing user state");

  // Clear user state
  useAuthStore.getState().setUser(null);
};

// Intercept responses to check for 401 and 403 errors, refresh token and retry the request
createAuthRefreshInterceptor(axios, handleAuthError, {
  statusCodes: [401, 403],
  retryInstance: axios,
});

createAuthRefreshInterceptor(axiosForRefresh, handleRefreshError, {
  statusCodes: [401, 403],
  retryInstance: axiosForRefresh,
});
